import { useCallback } from 'react'
import { useModal } from '~/hooks/useModal'
import { BankUnavailable } from '../components/BankUnavailable'
import { DepositWithdrawModal } from '../components/DepositWithdrawModal'
import { useBankGates } from './useBankGates'

export type CashierAction = 'deposit' | 'withdraw'

/**
 * One door into deposit/withdraw, for every entry point that opens it.
 *
 * Each of those used to write the gates out for itself, and they had drifted apart. The header
 * checked `WITHDRAWAL_ENABLED` before opening the modal on its *deposit* tab, so switching
 * withdrawals off also took depositing away from every page that shows the menu bar — while the
 * dashboard, which only disabled its own Withdraw button, still let the same user deposit. Only the
 * withdraw and payout RPC methods are registered behind that flag (`bank/urls.py`), so deposits had
 * been working the whole time. Here the flag blocks withdrawing and nothing else.
 *
 * The email gate does apply to both, which is kept deliberately rather than inherited: it is the one
 * condition `BankUnavailable` can hand the user a way out of.
 */
export const useCashier = () => {
    const { setModal } = useModal()
    const { emailConfirmed, fundsFrozen, withdrawBlocked } = useBankGates()

    const open = useCallback(
        (action: CashierAction = 'deposit') => {
            if (!emailConfirmed) return setModal(<BankUnavailable reason='email' />)
            // Frozen first: `withdrawBlocked` is true for both, and collapsing them would tell a
            // player whose account is held that the whole platform is paused and their balance is
            // safe -- which is the one message that is wrong for them.
            if (action === 'withdraw' && fundsFrozen) return setModal(<BankUnavailable reason='frozen' />)
            if (action === 'withdraw' && withdrawBlocked) return setModal(<BankUnavailable reason='disabled' />)
            setModal(<DepositWithdrawModal action={action} />)
        },
        [setModal, emailConfirmed, fundsFrozen, withdrawBlocked]
    )

    return { open, withdrawBlocked }
}
